import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/components/AuthProvider";
import { ThemeProvider } from "@/design-system";

import Landing from "@/pages/Landing";
import Join from "@/pages/Join";
import AdminLogin from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import SelfSubmit from "@/pages/SelfSubmit";

import MemberShell from "@/pages/member/MemberShell";
import ForYou from "@/pages/member/ForYou";
import EventDetail from "@/pages/member/EventDetail";
import ChainPage from "@/components/member/ChainBits";
import DiscoverPage from "@/pages/member/DiscoverPage";
import VenuePage from "@/pages/member/VenuePage";
import OrganiserPage from "@/pages/member/OrganiserPage";
import SquadPage from "@/pages/member/SquadPage";
import SquadDetail from "@/pages/member/SquadDetail";
import ThreadView from "@/pages/member/ThreadView";
import InvitesPage from "@/pages/member/InvitesPage";
import NotificationsPage from "@/pages/member/NotificationsPage";
import SavedRedirect from "@/pages/member/SavedRedirect";
import ProfilePage from "@/pages/member/ProfilePage";
import MembershipPage from "@/pages/member/MembershipPage";
import JoinIntake from "@/pages/member/JoinIntake";
import { SavedPage, LikedPage } from "@/pages/member/SavedLikedPages";
import NewSubmit from "@/pages/member/NewSubmit";
import EditProfilePage from "@/pages/member/EditProfilePage";
import SignInPrompt from "@/pages/member/SignInPrompt";
import SignInRoute from "@/pages/member/SignInRoute";
import NewStoryPage from "@/pages/member/NewStoryPage";
import KibandaDashboard from "@/pages/member/KibandaDashboard";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import GoogleCallback from "@/pages/public/GoogleCallback";

import AdminLayout from "@/pages/admin/Layout";
import ReviewQueue from "@/pages/admin/ReviewQueue";
import Venues from "@/pages/admin/Venues";
import Leads from "@/pages/admin/Leads";
import Published from "@/pages/admin/Published";
import Settings from "@/pages/admin/Settings";
import Users from "@/pages/admin/Users";
import Sources from "@/pages/admin/Sources";
import RateEvent from "@/pages/admin/RateEvent";
import AdminOrganisers from "@/pages/admin/Organisers";
import Analytics from "@/pages/admin/Analytics";
import OpsPage from "@/pages/admin/OpsPage";

// Founder Part 1 §3 + Vania Msg 220 §1 — host-guarded router.
// One deployment, host-based split:
//   · admin.watuwako.com → /admin/* only (if the subdomain ever routes here).
//   · app.watuwako.com   → member app AND /admin/* (single-domain constraint).
//   · anything else      → the full split (preview, local, watu-backend.emergent.host).
function currentHost() {
  if (typeof window === "undefined") return "";
  return (window.location.hostname || "").toLowerCase();
}
function detectMode() {
  const h = currentHost();
  if (h === "admin.watuwako.com") return "admin_only";
  if (h === "app.watuwako.com") return "public_with_admin";
  return "full"; // preview + local + emergent.host
}

function AdminOnlyRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/" element={<Navigate to="/admin/queue" replace />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/queue" replace />} />
        <Route path="queue" element={<ReviewQueue />} />
        <Route path="venues" element={<Venues />} />
        <Route path="leads" element={<Leads />} />
        <Route path="published" element={<Published />} />
        <Route path="users" element={<Users />} />
        <Route path="sources" element={<Sources />} />
        <Route path="rate/:eventId" element={<RateEvent />} />
        <Route path="organisers" element={<AdminOrganisers />} />
        <Route path="settings" element={<Settings />} />
        <Route path="ops" element={<OpsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/admin/queue" replace />} />
    </Routes>
  );
}

function PublicOnlyRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="/app" element={<MemberShell />}>
        <Route index element={<ForYou />} />
        <Route path="event/:id" element={<EventDetail />} />
        <Route path="chain/:id" element={<ChainPage />} />
        <Route path="venue/:id" element={<VenuePage />} />
        <Route path="organiser/:handle" element={<OrganiserPage />} />
        <Route path="people/:handle" element={<ProfilePage mode="public" />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="new" element={<NewSubmit />} />
        <Route path="squad" element={<SquadPage />} />
        <Route path="squad/:squadId" element={<SquadDetail />} />
        <Route path="yap/:threadId" element={<ThreadView />} />
        <Route path="invites" element={<InvitesPage />} />
        <Route path="me" element={<ProfilePage mode="me" />} />
        <Route path="me/edit" element={<EditProfilePage />} />
        <Route path="new-story" element={<NewStoryPage />} />
        <Route path="saved" element={<SavedRedirect />} />
        <Route path="liked" element={<LikedPage />} />
        <Route path="signin" element={<SignInRoute />} />
        <Route path="membership" element={<MembershipPage />} />
        <Route path="kibanda" element={<KibandaDashboard />} />
      </Route>
      <Route path="/app/welcome" element={<JoinIntake />} />
      <Route path="/submit" element={<SelfSubmit />} />
      <Route path="/join" element={<Join />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

function FullRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="/app" element={<MemberShell />}>
        <Route index element={<ForYou />} />
        <Route path="event/:id" element={<EventDetail />} />
        <Route path="chain/:id" element={<ChainPage />} />
        <Route path="venue/:id" element={<VenuePage />} />
        <Route path="organiser/:handle" element={<OrganiserPage />} />
        <Route path="people/:handle" element={<ProfilePage mode="public" />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="new" element={<NewSubmit />} />
        <Route path="squad" element={<SquadPage />} />
        <Route path="squad/:squadId" element={<SquadDetail />} />
        <Route path="yap/:threadId" element={<ThreadView />} />
        <Route path="invites" element={<InvitesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="me" element={<ProfilePage mode="me" />} />
        <Route path="me/edit" element={<EditProfilePage />} />
        <Route path="new-story" element={<NewStoryPage />} />
        <Route path="saved" element={<SavedRedirect />} />
        <Route path="liked" element={<LikedPage />} />
        <Route path="membership" element={<MembershipPage />} />
        <Route path="kibanda" element={<KibandaDashboard />} />
      </Route>
      <Route path="/marketing" element={<Landing />} />
      <Route path="/join" element={<Join />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/app/welcome" element={<JoinIntake />} />
      <Route path="/app/signin" element={<SignInRoute />} />
      <Route path="/submit" element={<SelfSubmit />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/queue" replace />} />
        <Route path="queue" element={<ReviewQueue />} />
        <Route path="venues" element={<Venues />} />
        <Route path="leads" element={<Leads />} />
        <Route path="published" element={<Published />} />
        <Route path="users" element={<Users />} />
        <Route path="sources" element={<Sources />} />
        <Route path="rate/:eventId" element={<RateEvent />} />
        <Route path="organisers" element={<AdminOrganisers />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="settings" element={<Settings />} />
        <Route path="ops" element={<OpsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

function PublicWithAdminRoutes() {
  // Vania Msg 220 §1 — one custom domain, so the desk lives under
  // app.watuwako.com/admin. AdminLayout enforces is_admin against the
  // allow-list; anyone else lands on /admin/login.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="/app" element={<MemberShell />}>
        <Route index element={<ForYou />} />
        <Route path="event/:id" element={<EventDetail />} />
        <Route path="chain/:id" element={<ChainPage />} />
        <Route path="venue/:id" element={<VenuePage />} />
        <Route path="organiser/:handle" element={<OrganiserPage />} />
        <Route path="people/:handle" element={<ProfilePage mode="public" />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="new" element={<NewSubmit />} />
        <Route path="squad" element={<SquadPage />} />
        <Route path="squad/:squadId" element={<SquadDetail />} />
        <Route path="yap/:threadId" element={<ThreadView />} />
        <Route path="invites" element={<InvitesPage />} />
        <Route path="me" element={<ProfilePage mode="me" />} />
        <Route path="me/edit" element={<EditProfilePage />} />
        <Route path="new-story" element={<NewStoryPage />} />
        <Route path="saved" element={<SavedRedirect />} />
        <Route path="liked" element={<LikedPage />} />
        <Route path="signin" element={<SignInRoute />} />
        <Route path="membership" element={<MembershipPage />} />
        <Route path="kibanda" element={<KibandaDashboard />} />
      </Route>
      <Route path="/app/welcome" element={<JoinIntake />} />
      <Route path="/submit" element={<SelfSubmit />} />
      <Route path="/join" element={<Join />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/queue" replace />} />
        <Route path="queue" element={<ReviewQueue />} />
        <Route path="venues" element={<Venues />} />
        <Route path="leads" element={<Leads />} />
        <Route path="published" element={<Published />} />
        <Route path="users" element={<Users />} />
        <Route path="sources" element={<Sources />} />
        <Route path="rate/:eventId" element={<RateEvent />} />
        <Route path="organisers" element={<AdminOrganisers />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="settings" element={<Settings />} />
        <Route path="ops" element={<OpsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

function HostGuardedRouter() {
  const mode = detectMode();
  if (mode === "admin_only") return <AdminOnlyRoutes />;
  if (mode === "public_with_admin") return <PublicWithAdminRoutes />;
  return <FullRouter />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <HostGuardedRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
